import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  createMembership,
  deleteMembership,
  listMemberships,
  updateMembership,
} from "@/api/practitioners";
import type { MembershipKindInOrg, Practitioner } from "@/api/types";
import { usePermission } from "@/auth/usePermission";

const KINDS: MembershipKindInOrg[] = [
  "SALARIE",
  "LIBERAL",
  "VACATAIRE",
  "STAGIAIRE",
  "RESERVISTE",
];

type Props = {
  open: boolean;
  practitioner: Practitioner | null;
  onClose: () => void;
};

export function MembershipsPanel({ open, practitioner, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const canCreate = usePermission("membership:create");
  const canDelete = usePermission("membership:delete");
  const canUpdate = usePermission("membership:update");

  const [showAdd, setShowAdd] = useState(false);
  const [newOrgId, setNewOrgId] = useState<string>("");
  const [newKind, setNewKind] = useState<MembershipKindInOrg>("SALARIE");
  const [newFte, setNewFte] = useState<number>(100);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setShowAdd(false);
      setServerError(null);
      setNewOrgId("");
      setNewKind("SALARIE");
      setNewFte(100);
    }
  }, [open, practitioner?.id]);

  const memberships = useQuery({
    queryKey: ["memberships", practitioner?.id],
    queryFn: () =>
      practitioner ? listMemberships(practitioner.id) : Promise.resolve([]),
    enabled: open && Boolean(practitioner),
  });

  const orgs = useQuery({
    queryKey: ["organizations-all-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
    enabled: open && canCreate,
  });

  const addMutation = useMutation({
    mutationFn: () =>
      practitioner
        ? createMembership(practitioner.id, {
            organization_id: newOrgId,
            kind: newKind,
            fte_percent: newFte,
          })
        : Promise.reject(new Error("no practitioner")),
    onSuccess: () => {
      setShowAdd(false);
      setNewOrgId("");
      qc.invalidateQueries({ queryKey: ["memberships", practitioner?.id] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
        ?.detail;
      if (detail === "duplicate_membership") {
        setServerError("Ce praticien est déjà affilié à cette organisation");
      } else {
        setServerError(typeof detail === "string" ? detail : (e as Error).message);
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (membershipId: string) =>
      practitioner
        ? deleteMembership(practitioner.id, membershipId)
        : Promise.reject(new Error("no practitioner")),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["memberships", practitioner?.id] }),
  });

  const kindMutation = useMutation({
    mutationFn: ({ membershipId, kind }: { membershipId: string; kind: MembershipKindInOrg }) =>
      practitioner
        ? updateMembership(practitioner.id, membershipId, { kind })
        : Promise.reject(new Error("no practitioner")),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["memberships", practitioner?.id] }),
  });

  if (!practitioner) return null;

  const existingOrgIds = new Set(memberships.data?.map((m) => m.organization_id) ?? []);
  const availableOrgs = (orgs.data?.items ?? []).filter((o) => !existingOrgIds.has(o.id));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 } } } }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t("practitioners.memberships")}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {practitioner.display_name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {memberships.isLoading && (
          <Typography variant="body2" color="text.secondary">
            {t("common.loading")}
          </Typography>
        )}
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <List sx={{ flex: 1, overflow: "auto" }}>
          {(memberships.data ?? []).length === 0 && !memberships.isLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              —
            </Typography>
          )}
          {(memberships.data ?? []).map((m) => {
            const org = orgs.data?.items.find((o) => o.id === m.organization_id);
            return (
              <ListItem
                key={m.id}
                divider
                secondaryAction={
                  canDelete && (
                    <Tooltip title={t("practitioners.removeMembership")}>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("practitioners.removeMembership") + " ?")) {
                            removeMutation.mutate(m.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {org ? `${org.code} — ${org.name}` : m.organization_id}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {canUpdate ? (
                        // The contract kind drives the liberal/salaried split on
                        // the dashboard, and the XLSX import gives everyone the
                        // same default — it has to be correctable here.
                        <Select
                          size="small"
                          variant="standard"
                          value={m.kind}
                          disabled={kindMutation.isPending}
                          onChange={(e) =>
                            kindMutation.mutate({
                              membershipId: m.id,
                              kind: e.target.value as MembershipKindInOrg,
                            })
                          }
                          sx={{ minWidth: 130, fontSize: "0.8125rem" }}
                        >
                          {KINDS.map((k) => (
                            <MenuItem key={k} value={k}>
                              {t(`membershipKind.${k}`, { defaultValue: k })}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          size="small"
                          label={t(`membershipKind.${m.kind}`, { defaultValue: m.kind })}
                        />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        color={m.status === "ACTIVE" ? "success" : "default"}
                        label={t(`membershipStatus.${m.status}`, { defaultValue: m.status })}
                      />
                      <Tooltip title={t("glossary.fte.def")}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={m.fte_percent < 100 ? "warning" : "default"}
                          label={`${m.fte_percent}% ${t("practitioners.fte")}`}
                        />
                      </Tooltip>
                    </Stack>
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </ListItem>
            );
          })}
        </List>

        {canCreate && !showAdd && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setShowAdd(true)}
            sx={{ mt: 2 }}
          >
            {t("practitioners.addMembership")}
          </Button>
        )}

        {canCreate && showAdd && (
          <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("nav.organizations")}</InputLabel>
                <Select
                  label={t("nav.organizations")}
                  value={newOrgId}
                  onChange={(e) => setNewOrgId(e.target.value)}
                >
                  {availableOrgs.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.code} — {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t("practitioners.kind")}</InputLabel>
                <Select
                  label={t("practitioners.kind")}
                  value={newKind}
                  onChange={(e) => setNewKind(e.target.value as MembershipKindInOrg)}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(`membershipKind.${k}`, { defaultValue: k })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label={t("practitioners.ftePercent")}
                value={newFte}
                onChange={(e) =>
                  setNewFte(Math.max(1, Math.min(100, Number(e.target.value) || 100)))
                }
                inputProps={{ min: 1, max: 100, step: 5 }}
                helperText={t("practitioners.fteHelp")}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => setShowAdd(false)} disabled={addMutation.isPending}>
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="contained"
                  disabled={addMutation.isPending || !newOrgId}
                  onClick={() => addMutation.mutate()}
                >
                  {t("common.save")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
